import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { RotateCcw, RefreshCw, Package, X, Upload, Clock, CheckCircle, XCircle, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ImageUpload from '../../components/ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RETURN_STATUS = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle }
};

export const AccountReturns = () => {
  const [searchParams] = useSearchParams();
  const [returns, setReturns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    return_type: 'return',
    reason: '',
    additional_notes: '',
    images: []
  });

  useEffect(() => {
    loadReturns();
    loadDeliveredOrders();
    
    // Check if order param exists to auto-open modal
    const orderId = searchParams.get('order');
    if (orderId) {
      loadOrderForReturn(orderId);
    }
  }, [searchParams]);

  const loadReturns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/account/returns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReturns(response.data.returns || []);
    } catch (error) {
      console.error('Error loading returns:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveredOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/account/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter only delivered orders that don't have a return request
      const deliveredOrders = (response.data.orders || []).filter(
        order => order.order_status === 'delivered'
      );
      setOrders(deliveredOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadOrderForReturn = async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/account/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.order?.order_status === 'delivered') {
        setSelectedOrder(response.data.order);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error loading order:', error);
    }
  };

  const openReturnModal = (order) => {
    setSelectedOrder(order);
    setFormData({
      return_type: 'return',
      reason: '',
      additional_notes: '',
      images: []
    });
    setShowModal(true);
  };

  const handleSubmitReturn = async () => {
    if (!formData.reason) {
      toast.error('Please select a reason');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/account/returns`, {
        order_id: selectedOrder.id,
        ...formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Return request submitted successfully');
      setShowModal(false);
      loadReturns();
      loadDeliveredOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit return request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const availableOrders = orders.filter(
    order => !returns.some(r => r.order_id === order.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="account-returns">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-heading text-2xl font-light text-charcoal">Returns & Exchanges</h1>
        
        {availableOrders.length > 0 && (
          <div className="relative">
            <select
              onChange={(e) => {
                const order = orders.find(o => o.id === e.target.value);
                if (order) openReturnModal(order);
              }}
              className="h-10 px-4 pr-8 border border-charcoal/10 focus:border-gold outline-none bg-white text-sm appearance-none cursor-pointer"
              defaultValue=""
            >
              <option value="" disabled>Request Return for Order...</option>
              {availableOrders.map(order => (
                <option key={order.id} value={order.id}>
                  #{order.order_number} - {formatDate(order.created_at)}
                </option>
              ))}
            </select>
            <Plus className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-light pointer-events-none" />
          </div>
        )}
      </div>

      {/* Existing Returns */}
      {returns.length > 0 ? (
        <div className="space-y-4">
          {returns.map((request) => {
            const statusConfig = RETURN_STATUS[request.status] || RETURN_STATUS.pending;
            const StatusIcon = statusConfig.icon;
            
            return (
              <div key={request.id} className="bg-white border border-charcoal/10 p-6">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      {request.return_type === 'exchange' ? (
                        <RefreshCw className="w-5 h-5 text-purple-600" />
                      ) : (
                        <RotateCcw className="w-5 h-5 text-orange-600" />
                      )}
                      <span className="text-sm font-medium text-charcoal">
                        {request.return_type === 'exchange' ? 'Exchange' : 'Return'} Request
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs ${statusConfig.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                    </div>
                    <p className="text-sm text-charcoal-light">Order #{request.order_number}</p>
                    <p className="text-sm text-charcoal-light">Submitted: {formatDate(request.created_at)}</p>
                  </div>
                  
                  {request.refund_amount && request.status === 'completed' && (
                    <div className="text-right">
                      <p className="text-xs text-charcoal-light">Refund Amount</p>
                      <p className="font-heading text-lg text-green-600">{formatCurrency(request.refund_amount)}</p>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 p-4 bg-beige/50">
                  <p className="text-sm text-charcoal-light">Reason:</p>
                  <p className="text-sm text-charcoal">{request.reason}</p>
                  {request.additional_notes && (
                    <p className="text-sm text-charcoal-light mt-2">{request.additional_notes}</p>
                  )}
                </div>
                
                {request.admin_notes && (
                  <div className="mt-4 p-4 bg-blue-50">
                    <p className="text-sm font-medium text-blue-800">Admin Response:</p>
                    <p className="text-sm text-blue-700">{request.admin_notes}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-charcoal/10 p-12 text-center">
          <RotateCcw className="w-16 h-16 mx-auto text-charcoal-light mb-4" />
          <h3 className="font-heading text-xl font-light text-charcoal mb-2">No return requests</h3>
          <p className="text-charcoal-light mb-4">
            {availableOrders.length > 0 
              ? 'Select an order above to request a return or exchange'
              : 'You can request returns for delivered orders'}
          </p>
        </div>
      )}

      {/* Return Request Modal */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50">
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-charcoal/10 p-6 flex items-center justify-between">
              <h2 className="font-heading text-xl font-light text-charcoal">
                Request Return/Exchange
              </h2>
              <button onClick={() => setShowModal(false)} className="text-charcoal-light hover:text-charcoal">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Order Info */}
              <div className="bg-beige/50 p-4">
                <p className="text-sm font-medium text-charcoal">Order #{selectedOrder.order_number}</p>
                <p className="text-sm text-charcoal-light">Delivered: {formatDate(selectedOrder.delivered_at || selectedOrder.created_at)}</p>
                <p className="text-sm text-charcoal-light">{selectedOrder.items?.length} item(s) - {formatCurrency(selectedOrder.total_amount)}</p>
              </div>

              {/* Return Type */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Request Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFormData({ ...formData, return_type: 'return' })}
                    className={`p-4 border text-center transition-colors ${
                      formData.return_type === 'return'
                        ? 'border-gold bg-gold/10'
                        : 'border-charcoal/10 hover:border-gold/50'
                    }`}
                  >
                    <RotateCcw className={`w-6 h-6 mx-auto mb-2 ${formData.return_type === 'return' ? 'text-gold' : 'text-charcoal-light'}`} />
                    <p className="font-medium text-charcoal">Return</p>
                    <p className="text-xs text-charcoal-light">Get refund</p>
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, return_type: 'exchange' })}
                    className={`p-4 border text-center transition-colors ${
                      formData.return_type === 'exchange'
                        ? 'border-gold bg-gold/10'
                        : 'border-charcoal/10 hover:border-gold/50'
                    }`}
                  >
                    <RefreshCw className={`w-6 h-6 mx-auto mb-2 ${formData.return_type === 'exchange' ? 'text-gold' : 'text-charcoal-light'}`} />
                    <p className="font-medium text-charcoal">Exchange</p>
                    <p className="text-xs text-charcoal-light">Different size/color</p>
                  </button>
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Reason *
                </label>
                <select
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none bg-white"
                >
                  <option value="">Select a reason</option>
                  <option value="Wrong size">Wrong size</option>
                  <option value="Defective product">Defective product</option>
                  <option value="Not as described">Not as described</option>
                  <option value="Quality not satisfactory">Quality not satisfactory</option>
                  <option value="Changed mind">Changed mind</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Additional Details
                </label>
                <textarea
                  value={formData.additional_notes}
                  onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                  className="w-full h-24 px-4 py-3 border border-charcoal/10 focus:border-gold outline-none resize-none"
                  placeholder="Provide any additional details about your return/exchange request..."
                />
              </div>

              {/* Images */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Upload Images (Optional)
                </label>
                <ImageUpload
                  onImageUploaded={(url) => setFormData({ ...formData, images: [...formData.images, url] })}
                />
                {formData.images.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="relative w-16 h-16">
                        <img src={img} alt="" className="w-full h-full object-cover border border-charcoal/10" />
                        <button
                          onClick={() => setFormData({ 
                            ...formData, 
                            images: formData.images.filter((_, i) => i !== idx) 
                          })}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Policy Note */}
              <div className="p-4 bg-blue-50 text-sm text-blue-800">
                <p className="font-medium mb-1">Exchange Policy</p>
                <ul className="list-disc pl-4 space-y-1 text-blue-700">
                  <li>Exchange requests within 3 days of delivery</li>
                  <li>Products must be unused with original tags</li>
                  <li>Subject to stock availability</li>
                </ul>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-charcoal/10 p-6 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-charcoal/20 text-charcoal text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReturn}
                disabled={submitting || !formData.reason}
                className="px-6 py-2 bg-gold text-white text-sm disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountReturns;
